import './BuyNowButton.css';

import { WorkflowVersion } from '@foxden/version-controller-client';
import { EmailOutlined, Warning } from '@material-ui/icons';
import React, { useContext, useState } from 'react';

import { PaymentGraphqlClient } from '../../../../backend-client/paymentGraphqlBackend';
import { RatingQuotingClient } from '../../../../backend-client/ratingQuotingBackend';
import Spinner from '../../../../components/Spinner';
import { BusinessInfo } from '../../../../generated/rating-quoting-graphql';
import Download from '../../../../images/Download.svg';
import getEnv from '../../../../utils/getEnv';
import { Context, ContextValue } from '../../../../utils/store';
import { NewQuoteForApp } from '../../Quote';
import { AdditionalInsuredInfoListForApplication } from '../../QuoteTypes';
import PaymentMethod from '../PaymentMethod';
import QuoteLinkPopup from '../QuoteLinkPopup';
import getConsolidatedQuoteFileName, {
  lobTypeToFileLabel,
  sanitizeFileNamePart
} from './consolidatedQuoteFileName';
import Popup from './Popup';

const { REACT_APP_BUY_NOW_TRACKING_URL } = getEnv();
export interface QuoteForDownload {
  quoteId: string;
  quoteNumber: string;
  lobType: string;
}

export interface BuyNowButtonProps {
  quoteId: string;
  /** Every quote in the group, so "Download Quote" can fetch a PDF per product. */
  quotesForDownload: QuoteForDownload[];
  primaryApplicationId: string;
  applicationGroupId: string;
  applyNewQuoteIds: (newQuoteIds: NewQuoteForApp[]) => void;
  showSendQuoteButton: boolean;
  country: string;
  businessInfo: BusinessInfo;
  disabled: boolean;
  additionalInsuredInfoListByAppId?: Record<
    string,
    AdditionalInsuredInfoListForApplication['additionalInsuredInfoList']
  >;
  validateAdditionalInsured: () => boolean;
  versionData: WorkflowVersion;
  isCustomerAgreementActive: boolean;
  isAuthenticated: boolean;
  isAuthProviderLoading: boolean;
  paymentGraphqlClient: PaymentGraphqlClient | null;
  transactionType: string;
  quoteExpireDate: string;
  refetchBusinessInfo?: () => void;
}

const DOWNLOAD_ERROR_MESSAGE =
  'Sorry, we could not generate your quote document. Please try again.';

const FOXQUILT_TERMS_OF_USE = 'https://www.foxquilt.com/terms-of-use.html';
const FOXQUILT_PRIVACY_NOTICE = 'https://www.foxquilt.com/privacy-policy.html';

interface BuyNowButtonTrackingData {
  applicationId: string;
  munichQuoteId: string;
}
const buyNowButtonTracking = (
  buyNowButtonTrackingData: BuyNowButtonTrackingData
) => {
  try {
    fetch(REACT_APP_BUY_NOW_TRACKING_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(buyNowButtonTrackingData)
    });
  } catch (e) {
    // don't show error of this api call to client
  }
};

const BuyNowButton: React.FC<BuyNowButtonProps> = (props) => {
  const {
    quoteId,
    quotesForDownload,
    primaryApplicationId,
    applicationGroupId,
    applyNewQuoteIds,
    country,
    businessInfo,
    disabled,
    additionalInsuredInfoListByAppId,
    validateAdditionalInsured,
    versionData,
    isCustomerAgreementActive,
    isAuthenticated,
    isAuthProviderLoading,
    paymentGraphqlClient,
    transactionType,
    quoteExpireDate,
    refetchBusinessInfo
  } = props;
  const [showPopup, setShowPopup] = useState(false);
  const [displayModal, setDisplayModal] = useState(false);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  const isEndorsement = transactionType === 'Endorsement';
  const closePopup = () => {
    setShowPopup(false);
  };
  const context: ContextValue | null = useContext(Context);
  const setCustomerAgreementActive = context?.setCustomerAgreementActive;

  const buttonName = isEndorsement ? 'CHANGE POLICY' : 'BUY NOW';

  // Turn a base64 PDF into either a download or a new tab (broker / customer
  // agreement flow opens the PDF instead of downloading it).
  const saveOrOpenPdf = (base64Pdf: string, pdfFileName: string) => {
    // Decode base64 string to binary string
    const binaryString = atob(base64Pdf);
    // Create a byte array from the binary string
    const byteArray = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      byteArray[i] = binaryString.charCodeAt(i);
    }
    // Convert binary buffer to Blob
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    // Create object URL
    const url = window.URL.createObjectURL(blob);

    if (isCustomerAgreementActive) {
      window.open(url);
    } else {
      // Create a download link
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', pdfFileName); // File name
      document.body.appendChild(link);
      // Trigger click event to start download
      link.click();
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
  };

  const downloadPdf = async () => {
    setIsPdfLoading(true);
    setDownloadError('');
    const ratingQuotingClient = new RatingQuotingClient(
      versionData.quoteBackendVersion
    );
    // A group of two quotes downloads one consolidated document. A single
    // quote keeps the single-document path.
    try {
      if (quotesForDownload.length === 1) {
        const quote = quotesForDownload[0];
        const data = await ratingQuotingClient.generateQuotePdf(quote.quoteId);
        const pdfFileName = `Foxquilt_Quote_${sanitizeFileNamePart(
          businessInfo.companyName
        )}_${lobTypeToFileLabel(quote.lobType)}_${quote.quoteNumber}.pdf`;
        saveOrOpenPdf(data, pdfFileName);
      } else if (quotesForDownload.length === 2) {
        // Select the quotes by product, not by list position.
        const glQuote = quotesForDownload.find((q) => q.lobType === 'GL');
        const eoQuote = quotesForDownload.find((q) => q.lobType === 'EO');
        if (!glQuote || !eoQuote) {
          // A two-product group must be one GL quote and one E&O quote.
          // eslint-disable-next-line no-console
          console.warn(
            `a two-product group must be one GL and one E&O quote; got: ${quotesForDownload
              .map((q) => q.lobType)
              .join(', ')}`
          );
          setDownloadError(DOWNLOAD_ERROR_MESSAGE);
          return;
        }
        const data = await ratingQuotingClient.generateConsolidatedQuotePdf([
          glQuote.quoteId,
          eoQuote.quoteId
        ]);
        const pdfFileName = getConsolidatedQuoteFileName(
          businessInfo.companyName,
          eoQuote.quoteNumber,
          glQuote.quoteNumber
        );
        saveOrOpenPdf(data, pdfFileName);
      } else {
        // Only one or two products are supported. Warn and make no call.
        // eslint-disable-next-line no-console
        console.warn(
          `quote download supports one or two products only; got ${quotesForDownload.length}`
        );
        setDownloadError(DOWNLOAD_ERROR_MESSAGE);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('quote document download failed', err);
      setDownloadError(DOWNLOAD_ERROR_MESSAGE);
    } finally {
      setIsPdfLoading(false);
    }
  };

  const QuotePDF = (
    <>
      <button
        className="w-full mt-2 border-2 border-faded rounded-md bg-white hover:bg-background text-quartary font-semibold py-1 px-3 md:px-14 flex justify-center items-center disabled:cursor-wait"
        onClick={downloadPdf}
        disabled={isPdfLoading}
        data-testid="download-quote-button"
      >
        {isPdfLoading ? (
          <span
            className="flex justify-center items-center"
            data-testid="download-quote-loading"
          >
            <Spinner className="h-6 w-6 mr-2" />
            Loading...
          </span>
        ) : !isCustomerAgreementActive ? (
          <>
            <img className="h-auto w-auto inline mr-2" src={Download} />
            Download Quote
          </>
        ) : (
          'View Quote PDF'
        )}
      </button>
      {downloadError ? (
        <p
          className="text-red-600 font-semibold mt-2"
          role="alert"
          data-testid="download-quote-error"
        >
          {downloadError}
        </p>
      ) : null}
    </>
  );

  const clientUI = (
    <>
      <button
        disabled={disabled}
        className="w-full bg-tertiary hover:bg-tertiary-dark border-tertiary-dark rounded-md uppercase pointer font-bold px-14 py-3 mb-2 text-mobileFont md:text-xl text-white confirm-buy"
        onClick={() => {
          // Validate the Additional Insured information
          const validationResult = validateAdditionalInsured();
          if (validationResult) {
            setShowPopup(true);
            buyNowButtonTracking({
              applicationId: primaryApplicationId,
              munichQuoteId: quoteId
            }); // TODO: rename it later
          } else {
            const additionalInsuredEle = document.getElementById(
              'additional-insured-container'
            );
            additionalInsuredEle
              ? additionalInsuredEle.scrollIntoView({
                  behavior: 'smooth'
                })
              : null;
          }
        }}
      >
        {buttonName}
      </button>
      <p className="text-center text-xs mt-2 mx-auto">
        By clicking {buttonName}, you agree to Foxquilt&apos;s{' '}
        <a
          className="small-underline text-secondary"
          href={FOXQUILT_TERMS_OF_USE}
          target="_blank"
          rel="noreferrer"
        >
          Terms of Use
        </a>{' '}
        and{' '}
        <a
          className="small-underline text-secondary"
          href={FOXQUILT_PRIVACY_NOTICE}
          target="_blank"
          rel="noreferrer"
        >
          Privacy Notice
        </a>
      </p>
      <p className="text-center mt-2 text-xs mb-2">
        Your coverage will not be effective until payment is confirmed
      </p>
      <PaymentMethod />
      <div className="border-gray-300 border-t-2 border-b-2 py-4">
        <button
          className="w-full border-2 border-faded rounded-md bg-white hover:bg-background text-quartary font-semibold py-1 px-3 md:px-14"
          onClick={() => setDisplayModal(true)}
        >
          <EmailOutlined className="mb-1 mr-2" />
          Send Quote
        </button>
        {!isEndorsement ? QuotePDF : null}
      </div>
    </>
  );

  const brokerUI = (
    <>
      <div className="policy-warning flex flex-row items-center">
        <Warning
          style={{ color: '#FFD64E' }}
          fontSize="large"
          className="mr-2"
        />
        <span data-testid="policy-warning">
          Coverage is <b>NOT</b> bound, and a policy will <b>NOT</b> be issued
          until both the application is signed <b>AND</b> payment received.
        </span>
      </div>
      <div className="border-gray-300 border-t-2 border-b-2 py-4">
        {!isEndorsement ? QuotePDF : null}
      </div>
    </>
  );

  return (
    <div className="confirm-pay-container lg:max-w-xs text-center sm:mt-0">
      {!isCustomerAgreementActive ? clientUI : brokerUI}
      {showPopup ? (
        <Popup
          closeAction={closePopup}
          quoteId={quoteId}
          businessInfo={businessInfo}
          transactionType={transactionType}
          quoteExpireDate={quoteExpireDate}
          primaryApplicationId={primaryApplicationId}
          applicationGroupId={applicationGroupId}
          applyNewQuoteIds={applyNewQuoteIds}
          country={country}
          additionalInsuredInfoListByAppId={additionalInsuredInfoListByAppId}
          setShowPopup={setShowPopup}
          setCustomerAgreementActive={setCustomerAgreementActive}
          isAuthenticated={isAuthenticated}
          isAuthProviderLoading={isAuthProviderLoading}
          paymentGraphqlClient={paymentGraphqlClient}
          versionData={versionData}
        />
      ) : null}

      <QuoteLinkPopup
        displayModal={displayModal}
        handleClose={() => setDisplayModal(false)}
        applicationId={primaryApplicationId}
        applicationGroupId={applicationGroupId}
        applyNewQuoteIds={applyNewQuoteIds}
        businessInfo={businessInfo}
        additionalInsuredInfoListByAppId={additionalInsuredInfoListByAppId}
        refetchBusinessInfo={refetchBusinessInfo}
      />
    </div>
  );
};

export default BuyNowButton;
